import { useRef, useState, useEffect } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Flex,
  Box,
  Input,
  Button,
  Icon,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './index.css';
import CalendarIcon from '@/assets/icons/reports/Calendar';

import { QuickCalendar } from '../quick-calendar';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';

const today = new Date();

export const CustomDate = ({ selected = {}, onChange = () => {} }) => {
  const { feReportsV3 } = useKFlags();
  const ref = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const previousMonth = new Date(today.getTime());
  previousMonth.setDate(0);
  const [month, setMonth] = useState(previousMonth);

  const isSelected = selected?.from && selected?.to;

  const bg = isSelected
    ? feReportsV3
      ? 'primary-100'
      : 'primary-500'
    : 'base-white';
  const color = isSelected
    ? feReportsV3
      ? 'primary-600'
      : 'base-white'
    : 'secondary-700';

  // Initialize range from selected prop (localStorage) or default to today
  const [range, setRange] = useState(() => {
    if (isSelected) {
      return {
        from: new Date(selected.from),
        to: new Date(selected.to),
      };
    }
    return { from: today, to: today };
  });

  // Track if we've initialized from selected prop to avoid overriding user interactions
  const [hasInitialized, setHasInitialized] = useState(isSelected);

  // Update range when selected prop changes (e.g., when localStorage data loads)
  // but only if we haven't initialized yet or if the popover is closed
  useEffect(() => {
    if (isSelected && (!hasInitialized || !isOpen)) {
      // Convert to timestamps for comparison to avoid Date object reference issues
      const selectedFromTime = new Date(selected.from).getTime();
      const selectedToTime = new Date(selected.to).getTime();
      const currentFromTime = range.from?.getTime();
      const currentToTime = range.to?.getTime();

      // Only update if the values are actually different
      if (
        selectedFromTime !== currentFromTime ||
        selectedToTime !== currentToTime
      ) {
        const newRange = {
          from: new Date(selected.from),
          to: new Date(selected.to),
        };
        setRange(newRange);
        // Only update month if popover is closed to avoid interfering with user navigation
        if (!isOpen) {
          setMonth(new Date(selected.from));
        }
      }
      setHasInitialized(true);
    }
  }, [
    selected.from,
    selected.to,
    isSelected,
    hasInitialized,
    isOpen,
    range.from,
    range.to,
  ]);

  const onApply = () => {
    onChange(range);
    onClose();
  };

  const onChangeQuickCalender = (selected) => {
    if (selected?.id === 'today') {
      setRange({ from: new Date(), to: new Date() });
      setMonth(new Date());
      return;
    }
    if (selected?.id === 'yesterday') {
      setRange({ from: subDays(new Date(), 1), to: subDays(new Date(), 1) });
      setMonth(subDays(new Date(), 1));
      return;
    }

    if (selected?.id === 'this_week') {
      const from = startOfWeek(new Date());
      setRange({ from: from, to: new Date() });
      setMonth(from);
      return;
    }

    if (selected?.id === 'last_week') {
      const lastWeekFrom = startOfWeek(subDays(new Date(), 7));
      const lastWeekEnd = endOfWeek(subDays(new Date(), 7));
      setRange({ from: lastWeekFrom, to: lastWeekEnd });
      setMonth(lastWeekFrom);
      return;
    }

    if (selected?.id === 'this_month') {
      const lastMonthFrom = startOfMonth(new Date());
      setRange({ from: lastMonthFrom, to: new Date() });
      setMonth(lastMonthFrom);
      return;
    }

    if (selected?.id === 'last_month') {
      const lastMonthFrom = startOfMonth(subDays(new Date(), 30));
      const lastMonthEnd = endOfMonth(subDays(new Date(), 30));
      setRange({ from: lastMonthFrom, to: lastMonthEnd });
      setMonth(lastMonthFrom);
    }
  };

  const setDate = (range) => {
    if (!range) {
      setRange({ from: today, to: today });
      return;
    }
    if (!range?.from) {
      setRange({ ...range, from: range?.to });
      return;
    }

    if (!range?.to) {
      setRange({ ...range, to: range?.from });
      return;
    }

    setRange(range);
  };

  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  });

  return (
    <Flex
      bg={bg}
      ref={ref}
      h={feReportsV3 ? '-webkit-fill-available' : '38px'}
      zIndex={8}
    >
      <Popover placement="bottom" isOpen={isOpen}>
        <PopoverTrigger>
          <Flex
            display="inline-flex"
            alignItems="center"
            px="12px"
            cursor="pointer"
            onClick={isOpen ? onClose : onOpen}
            color={color}
            _hover={{ bg: 'secondary-25' }}
          >
            <Icon as={CalendarIcon} color={color} />
            <KText
              ml="4px"
              fontWeight={
                isSelected ? (feReportsV3 ? 550 : 700) : feReportsV3 ? 550 : 500
              }
              size={
                isSelected
                  ? feReportsV3
                    ? 'sm-regular'
                    : 'sm-semi-bold'
                  : 'sm-regular'
              }
              color={color}
            >
              {isSelected
                ? `${format(new Date(selected?.from), 'dd MMM yy')} - ${format(
                    new Date(selected?.to),
                    'dd MMM yy'
                  )}`
                : 'Custom'}
            </KText>
          </Flex>
        </PopoverTrigger>
        <PopoverContent
          w="800px"
          {...(feReportsV3
            ? {
                right: '20px',
              }
            : { left: '170px' })}
        >
          <Flex w="800px">
            <Box width="150px" borderRight="1px solid #EAECF0">
              <QuickCalendar
                options={[
                  { label: 'Today', id: 'today' },
                  { label: 'Yesterday', id: 'yesterday' },
                  { label: 'This week', id: 'this_week' },
                  { label: 'Last week', id: 'last_week' },
                  { label: 'This month', id: 'this_month' },
                  { label: 'Last month', id: 'last_month' },
                ]}
                onChange={onChangeQuickCalender}
              />
            </Box>
            <Flex flexDirection="column" flex="1">
              <Box flex="1">
                <DayPicker
                  mode="range"
                  numberOfMonths={2}
                  month={month}
                  onMonthChange={setMonth}
                  selected={range}
                  onSelect={setDate}
                  disabled={{ after: today }}
                />
              </Box>
              <Flex
                borderTop="1px solid #EAECF0"
                height="64px"
                alignItems="center"
                px="16px"
              >
                <Flex flex="1" alignItems="center">
                  <Input
                    width="136px"
                    value={format(new Date(range?.from), 'dd/MMM/yyyy')}
                    pointerEvents="none"
                    onChange={() => {}}
                  />
                  <KText px="8px">-</KText>
                  <Input
                    width="136px"
                    value={format(new Date(range?.to), 'dd/MMM/yyyy')}
                    pointerEvents="none"
                    onChange={() => {}}
                  />
                </Flex>
                <Box>
                  <Button mr="8px" variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button onClick={onApply} variant="primary">
                    Apply
                  </Button>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </PopoverContent>
      </Popover>
    </Flex>
  );
};
